import {Back, Power0, Power2, Power3, Power4} from "gsap";
import {IReelConfig} from "../../../core/reel/IData/IReelData";
import {IWinRatios} from "./IWinConfig";
import {CustomEase} from "gsap/dist/CustomEase";
import {ReelSetBuilder} from "../../../core/utils/ReelSetBuilder"
import {ISymbolDim} from "../../../core/reel/symbol/config/IReelSymbolStruct";

interface IGameDim {
    readonly landscape: {
        WIDTH: number;
        HEIGHT: number;
    };
    readonly portrait: {
        WIDTH: number;
        HEIGHT: number;
    };
}

class IBaseConfig {
    public static BASE_DIM: IGameDim;
    public static DEFAULT_SYMBOL_DIM: ISymbolDim;
    public static REEL_ENGINE_CONFIG: IReelConfig;
    public static WIN_RATIOS: IWinRatios;
    public static NO_WIN_GAME_DATA: any;
}

export class GameConfig implements IBaseConfig {


    // public static instance:GameConfig = new GameConfig();
    public static BASE_DIM: IGameDim = {
        landscape: {
            WIDTH: 1600, HEIGHT: 1280
        },
        portrait: {
            WIDTH: 680, HEIGHT: 1600
        }
    }

    public static DEFAULT_SYMBOL_DIM: ISymbolDim = {
        WIDTH: 112,
        HEIGHT: 131,
        PADDING_X: 0,
        PADDING_Y: 0,
        SCALE: new PIXI.Point(1, 1)
    };

    // **  Reel Config ** //
    public static REEL_ENGINE_CONFIG: IReelConfig = {
        // How many symbols should be displayed as part of the winArea and how many reels there are.
        WINDOW_AMOUNT: 4,

        // How many extra Symbols will be added for the stop reel. Increasing the distance for the reel to stop which simplifies other easing stops.
        STOP_DISTANCE: 0,

        // Define Reelsets for the reels to use.
        REEL_SETS: ReelSetBuilder.createSpinReels({
                amountOfReels: 5,
                maxVisibleSize: 5,
                minimumSize: 50,
                fillerSymbols: {
                    amount: 5,
                    type: ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
                    maxAmountInView: 1
                }
            },
            [
                {
                    name: "NORMAL",
                    symbols: [
                        {type: "S", amount: [0, 1, 1, 1, 1, 0], excludeWithSymbol: ["M"], maxAmountInView: 1},
                        {type: "W", amount: [0, 3, 3, 3, 3, 0], excludeWithSymbol: ["M,S"], maxAmountInView: 1},
                    ]
                },
                {
                    name: "RESPIN",
                    symbols: [
                        {type: "W", amount: [0, 3, 3, 3, 3, 3], excludeWithSymbol: ["M,S"], maxAmountInView: 1},
                    ]
                },

                {
                    name: "NORMAL_AVALANCHE",
                    symbols: [
                        {type: "S", amount: 3, maxAmountInView: 1},
                    ]
                },
                {
                    name: "FREESPIN_AVALANCHE",
                    symbols: [
                        {type: "S", amount: 3, maxAmountInView: 1},
                    ]
                },
                {
                    name: "FREESPIN",
                    symbols: [
                        {type: "S", amount: 3, maxAmountInView: 1},
                    ]
                },
                {
                    name: "SUPER_FREESPIN",
                    symbols: [
                        {type: "S", amount: 3, maxAmountInView: 1},
                    ]
                },
                {
                    name: "SUPER_FREESPIN_AVALANCHE",
                    symbols: [
                        {type: "S", amount: 3, maxAmountInView: 1},
                    ]
                },
                {
                    name: "SUPER_FREESPIN_END",
                    symbols: [
                        {type: "S", amount: 3, maxAmountInView: 1},
                    ]
                }
            ]
        ),

        DIM: {
            PADDING_X: 0,
            PADDING_Y: 0,
            WIDTH: 112, // symWidth
            HEIGHT: 131 * 4, // SymHeight * height
        },
        // Define the duration for specific states.
        DURATION: {

            START: 0.5,
            FAST_START: 0.3,
            SPIN: 0.15 / 5,
            FAST_SPIN: 0.1 / 5,

            STOP: 0.5,
            FAST_STOP: 0.25,
            QUICK_STOP: 0.15,

            ATTENTION_STOP: 1.8,

            // Define the minimum time from the start -> stop;
            MINIMUM_SPIN_TIME: 3, // Normal
            MINIMUM_FAST_SPIN_TIME: 1, // Fast

            EXPAND: 0.25
        },
        // Define the delay for specific states.
        DELAY: {
            START: 0.065,
            FAST_START: 0.03,
            STOP: 0.65,
            FAST_STOP: 0.1,
            QUICK_STOP: 0.01,
        },
        // Define the Easeing for specific states.
        EASING: {
            START: Back.easeIn.config(1),
            FAST_START: Back.easeIn.config(0.5),
            // STOP: Power3.easeOut,
            STOP: Back.easeInOut.config(1),
            DROP: Power2.easeIn,
            // START_TEASER: Back.easeInOut.config(0.5),
            STOP_AVALANCHE: Back.easeInOut.config(0.5),

            FAST_STOP: Back.easeInOut.config(0.7),
            QUICK_STOP: Back.easeInOut.config(0.7),

            ATTENTION_STOP: Back.easeOut.config(0.2),
            // FAST_STOP: Elastic.easeOut.config(0.8, 0.55),
            // QUICK_STOP:  Elastic.easeOut.config(0.8, 0.55),

            EXPAND: Back.easeInOut.config(1.7),
            // NUDGE: CustomEase.create("nudge", "M0,0 C0,0 0.264,-0.156 0.3,0 0.321,0.084 0.559,0.91 0.6,1 0.628,1.062 0.737,1.02 0.8,1 0.88,0.974 1,1 1,1"),
        }
    }

    public static WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                duration: 0,
            },
            {
                ratio: 1.01,
                duration: 0.3,
            },
            {
                ratio: 3,
                duration: 0.4,
            },
            {
                ratio: 5,
                duration: 0.8,
            }
        ],
        bigWin: [
            {
                ratio: 15,
                duration: 5.600,
            },
            {
                ratio: 25,
                duration: 6.203,
            },
            {
                ratio: 50,
                duration: 6.203,
            },
            {
                ratio: 100,
                duration: 6.203,
            },
            {
                ratio: 250,
                duration: 12.390,
            }
          
        ], SFSBigWin: [
            {
                ratio: 25,
                duration: 5.600,
            },
            {
                ratio: 50,
                duration: 6.203,
            },
            {
                ratio: 100,
                duration: 6.203,
            },
            {
                ratio: 250,
                duration: 6.203,
            },
            {
                ratio: 500,
                duration: 12.390,
            }
          
        ]
    };

    public static WIN_CAP_PROBABILITY = [
        {
            level: 0,
            name: 'Extremely Low',
            min: 0,
            max: 8
        },
        {
            level: 1,
            name: 'Very Low',
            min: 9,
            max: 20
        },
        {
            level: 2,
            name: 'Low',
            min: 21,
            max: 39
        },
        {
            level: 3,
            name: 'Medium',
            min: 40,
            max: 59
        },
        {
            level: 4,
            name: 'High',
            min: 60,
            max: 69
        },
        {
            level: 5,
            name: 'Very High',
            min: 70,
            max: 89
        },
        {
            level: 6,
            name: 'Extremely High',
            min: 90,
            max: 100
        }
    ];

    public static NO_WIN_GAME_DATA = {
        "reels": [
            [
                "M1",
                "M5",
                "L1",
                "M3"
            ],
            [
                "M2",
                "M2",
                "L2",
                "L3"
            ],
            [
                "M3",
                "M1",
                "L3",
                "M1"
            ],
            [
                "M4",
                "L1",
                "M2",
                "M3"
            ],
            [
                "M5",
                "L3",
                "L1",
                "L2"
            ]
        ],
        "evaluatedArea": [
            [
                "M1",
                "M5",
                "L1",
                "M3"
            ],
            [
                "M2",
                "M2",
                "L2",
                "L3"
            ],
            [
                "M3",
                "M1",
                "L3",
                "M1"
            ],
            [
                "M4",
                "L1",
                "M2",
                "M3"
            ],
            [
                "M5",
                "L3",
                "L1",
                "L2"
            ]
        ],
        "reelsAfterCollapse": [
            [
                "M1",
                "M5",
                "L1",
                "M3"
            ],
            [
                "M2",
                "M2",
                "L2",
                "L3"
            ],
            [
                "M3",
                "M1",
                "L3",
                "M1"
            ],
            [
                "M4",
                "L1",
                "M2",
                "M3"
            ],
            [
                "M5",
                "L3",
                "L1",
                "L2"
            ]
        ],
        "betWayWins": [],
        "totalSpinWinnings": 0,
        "accumulatedRoundWin": 0,
        "thisSpinWinning": 0,
        "playedBetValue": 1,
        "mode": "NORMAL",
        "nextMode": "NORMAL",
        "numberOfFreespinsPlayed": 0,
        "freespinsLeft": 0,
        "addedNumberOfFreespinsThisSpin": 0,
        "symbolSizes": [
            [
                1,
                1,
                1,
                1
            ],
            [
                1,
                1,
                1,
                1
            ],
            [
                1,
                1,
                1,
                1
            ],
            [
                1,
                1,
                1,
                1
            ],
            [
                1,
                1,
                1,
                1
            ]
        ],
        "wasFeatureBuy": false,
        "brokeTheBank": false,
        "winMultiplier": 1,
        "nextWinMultiplier": 1,
        "nextLevel": 0,
        "currentLevel": 0,
        "collectedSymbols": 0,
        "upgradeLevel": false,
        "numberOfDroppedScatter": 0,
        "cutIndex": [
            -1,
            -1,
            -1,
            -1,
            -1
        ],
        "soulWildsLandedPositions": {},
        "collectedSouls": 0,
        "nextCollectedSouls": 0,
        "enhancedBet": false,
        "playerSelect": -1,
        "positions": [
            [
                false,
                false,
                false,
                false
            ],
            [
                false,
                false,
                false,
                false
            ],
            [
                false,
                false,
                false,
                false
            ],
            [
                false,
                false,
                false,
                false
            ],
            [
                false,
                false,
                false,
                false
            ]
        ],
        "winCapProbabilityForTheRightSideChoice": 0
    }
}
